import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { eligibleLinkedInviteMembers, inviteTargetLabel } from './inviteFlow.js';
import { useAppState } from './AppState.js';
import { EmptyState, ErrorPanel, LoadingPanel, PageIntro, PanelHeader } from './components.js';
import { mapError } from './errors.js';
import { useInvalidateQueries, useLeagueMembers, useRequiredLeagueId, useSyncLeagueFromRoute } from './hooks.js';
import { formatDate } from '../lib/formatting.js';

export function AdminInvitesPage() {
  const leagueId = useRequiredLeagueId();
  useSyncLeagueFromRoute(leagueId);
  const { client, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();
  const invitesQuery = useQuery({
    queryKey: ['invites', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getInvites(leagueId)).invites,
  });
  const seasonsQuery = useQuery({
    queryKey: ['seasons', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getSeasons(leagueId)).seasons,
  });
  const membersQuery = useLeagueMembers(leagueId);
  const [form, setForm] = useState({
    scope: 'league',
    seasonId: '',
    playerId: '',
    expiresInDays: '7',
    maxUses: '0',
    startingHandicap: '',
  });

  const createMutation = useMutation({
    mutationFn: async () =>
      client!.createInvite(leagueId, {
        ...(form.startingHandicap.trim() ? { startingHandicap: Number(form.startingHandicap) } : {}),
        scope: form.scope as 'league' | 'season',
        expiresInDays: Number(form.expiresInDays || 7),
        maxUses: Number(form.maxUses || 0),
        ...(form.scope === 'season' && form.seasonId ? { seasonId: form.seasonId } : {}),
        ...(form.playerId ? { playerId: form.playerId } : {}),
      }),
    onSuccess: async () => {
      setBanner('success', 'Invite created.');
      await invalidate(['invites', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const revokeMutation = useMutation({
    mutationFn: async (inviteId: string) => client!.revokeInvite(leagueId, inviteId),
    onSuccess: async () => {
      setBanner('success', 'Invite revoked.');
      await invalidate(['invites', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  if (invitesQuery.isLoading || seasonsQuery.isLoading || membersQuery.isLoading) {
    return <LoadingPanel title="Invite Links" message="Loading invite data." />;
  }

  if (invitesQuery.isError || seasonsQuery.isError || membersQuery.isError || !invitesQuery.data || !seasonsQuery.data || !membersQuery.data) {
    return <ErrorPanel title="Invite Links" message={mapError(invitesQuery.error ?? seasonsQuery.error ?? membersQuery.error)} />;
  }

  const joinBase = `${window.location.origin}/join/`;
  const seasonNameById = new Map(seasonsQuery.data.map((season) => [season.id, season.name]));
  const membersByPlayerId = new Map(membersQuery.data.map((member) => [member.player_id, member]));
  const targetableMembers = eligibleLinkedInviteMembers(membersQuery.data);

  return (
    <>
      <PageIntro
        title="Invite Links"
      />
      <section className="grid two">
        <article className="panel panel-accent">
          <PanelHeader title="Create Invite" />
          <p className="muted">
            League invite: grants league access only. Season invite: grants league access and enrolls the player into
            the selected season. Player-link invite: links a new login to an existing unlinked league member.
          </p>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (form.scope === 'season' && !form.seasonId) {
                setBanner('error', 'Season invite scope requires selecting a season.');
                return;
              }
              void createMutation.mutateAsync();
            }}
          >
            <div className="row">
              <div className="field">
                <label>Scope</label>
                <select value={form.scope} onChange={(event) => setForm((current) => ({ ...current, scope: event.target.value, seasonId: event.target.value === 'season' ? current.seasonId : '' }))}>
                  <option value="league">League</option>
                  <option value="season">Season</option>
                </select>
              </div>
              <div className="field">
                <label>Season</label>
                <select
                  disabled={form.scope !== 'season' || seasonsQuery.data.length === 0}
                  required={form.scope === 'season'}
                  value={form.seasonId}
                  onChange={(event) => setForm((current) => ({ ...current, seasonId: event.target.value }))}
                >
                  <option value="">Select season...</option>
                  {seasonsQuery.data.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Link To Existing Player</label>
              <select
                value={form.playerId}
                disabled={targetableMembers.length === 0}
                onChange={(event) => setForm((current) => ({ ...current, playerId: event.target.value }))}
              >
                <option value="">No player link</option>
                {targetableMembers.map((member) => (
                  <option key={member.player_id} value={member.player_id}>
                    {member.player?.name || member.player_id}
                    {member.player?.email ? ` (${member.player.email})` : ''}
                  </option>
                ))}
              </select>
              <p className="muted">
                Optional. Select an unlinked league member if this invite should attach a new login to that player
                record.
              </p>
            </div>
            <div className="row">
              <div className="field">
                <label>Expires In (days)</label>
                <input type="number" min={1} max={30} value={form.expiresInDays} onChange={(event) => setForm((current) => ({ ...current, expiresInDays: event.target.value }))} />
              </div>
              <div className="field">
                <label>Max Uses (0 = unlimited)</label>
                <input type="number" min={0} max={10000} value={form.maxUses} onChange={(event) => setForm((current) => ({ ...current, maxUses: event.target.value }))} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="invite-starting-handicap">League starting handicap</label>
              <input id="invite-starting-handicap" type="number" min={-10} max={27} step="0.1"
                value={form.startingHandicap} onChange={(event) => setForm((current) => ({ ...current, startingHandicap: event.target.value }))} />
              <p className="muted">Required for new members without league history. Existing members carry their league index.</p>
            </div>
            <div className="inline-actions">
              <button type="submit" disabled={createMutation.isPending}>
                Create Invite
              </button>
            </div>
          </form>
        </article>

        <article className="panel">
          <PanelHeader title={`Active Invites (${invitesQuery.data.length})`} />
          {invitesQuery.data.length === 0 ? (
            <EmptyState title="No invites yet" message="Create a league, season, or linked-player invite to populate this list." />
          ) : (
            <div className="card-list">
              {invitesQuery.data.map((invite) => {
                const scope = invite.scope === 'season' ? 'season' : 'league';
                const targetLabel = inviteTargetLabel(invite, membersByPlayerId, seasonNameById);
                const linkedPlayerLabel = invite.player_id ? ` | Player Link: ${targetLabel}` : '';
                const joinLink = `${joinBase}${invite.token}`;
                return (
                  <div className="card invite-card" key={invite.id}>
                    <p>
                      <strong>Token:</strong> <code>{invite.token}</code>
                    </p>
                    <p className="muted">
                      Scope: {scope === 'season' ? 'Season' : 'League'} | Target:{' '}
                      {invite.player_id && scope !== 'season' ? 'Existing player' : targetLabel}
                      {linkedPlayerLabel}
                    </p>
                    <p className="muted">Expires: {formatDate(invite.expires_at)}</p>
                    <p>
                      <a target="_blank" rel="noreferrer" href={joinLink}>
                        {joinLink}
                      </a>
                    </p>
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="secondary"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(joinLink);
                            setBanner('success', 'Invite link copied.');
                          } catch {
                            setBanner('warn', 'Clipboard unavailable. Copy manually from the displayed link.');
                          }
                        }}
                      >
                        Copy Link
                      </button>
                      <button type="button" className="danger" onClick={() => void revokeMutation.mutateAsync(invite.id)}>
                        Revoke
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </article>
      </section>
    </>
  );
}
